import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPost } from '~/util/api'
import { Button } from '~/lib/ui/button'
import { Textarea } from '~/lib/ui/textarea'
import useTranslation from '~/components/shared/TranslationProvider'
import { Input } from '~/lib/ui/input'
import { Label } from '~/lib/ui/label'
import { useToast } from '~/lib/ui/toast'
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '~/lib/ui/select'
import { useDropzone } from 'react-dropzone'
import { cn } from '~/lib/utils'
import { Modal, Stack } from '~/components/_layouts'
import { UserIcon } from '@heroicons/react/24/outline'

const Dropzone = ({ setSelectedFile, className, children }) => {
  const baseRootClassName = cn(
    'min-h-[10rem] p-4 border-black/50 rounded-lg border-dashed border-2 text-center content-center justify-items-center cursor-pointer',
    className,
  )
  const { toast } = useToast()
  const { t } = useTranslation()

  const { getRootProps, getInputProps, isDraggingOver } = useDropzone({
    maxFiles: 1,
    accept: {
      '*/*/pdf': ['.pdf'],
      '*/*/msword': ['.doc'],
      '*/*/document': ['.docx'],
    },
    onDropAccepted: (incomingFiles) => {
      setSelectedFile(incomingFiles[0])
    },
    onDropRejected: (incomingFiles) => {
      const {
        file: { type },
        errors,
      } = incomingFiles[0]
      const description = errors.map((e) => ` • ${e.message}`).join('\n')
      console.error('File upload rejected:', description)
      if (type) {
        toast({
          description,
          title: 'Resume attachment failed',
          variant: 'destructive',
          duration: 15_000,
        })
      }
    },
  })

  const rootClassName = isDraggingOver
    ? cn(baseRootClassName, 'border-primary bg-lily-blue')
    : baseRootClassName

  return (
    <div className="pb-4">
      <div className="mb-2 flex flex-row items-center justify-start">
        <div className="text-lg font-medium">Resume Attachments</div>
      </div>
      <div {...getRootProps({ className: rootClassName })}>
        <input {...getInputProps()} />
        <div>
          {children}
          <em>(Files must be in .pdf, .doc, or .docx format)</em>
        </div>
      </div>
      <div className="">{t('resume_subtext')}</div>
    </div>
  )
}

const UpdateCandidateProfileDialog = ({
  data,
  showUpdateProfileDialog,
  toggleShowUpdateProfileDialog,
}) => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const {
    interest: interestDefault,
    seniority_level: seniorityLevelDefault,
    industry: desiredIndustryDefault,
    job_title: desiredJobTitleDefault,
    linked_in_profile_url: linkedInProfileDefault,
    career_history: careerHistoryDefault,
  } = data
  const [interest, setInterest] = useState(interestDefault || '')
  const [seniorityLevel, setSeniorityLevel] = useState(
    seniorityLevelDefault || '',
  )
  const [desiredJobTitle, setDesiredJobTitle] = useState(
    desiredJobTitleDefault || '',
  )
  const [linkedInProfile, setLinkedInProfile] = useState(
    linkedInProfileDefault || '',
  )
  const [desiredIndustry, setDesiredIndustry] = useState(
    desiredIndustryDefault || '',
  )
  const [careerHistory, setCareerHistory] = useState(careerHistoryDefault || '')
  const [selectedFile, setSelectedFile] = useState(null)

  const { t } = useTranslation()
  const interestKeys = Object.keys(t('interests') || {})
  const seniorityKeys = Object.keys(t('seniority_levels') || {})

  const mutationFn = async () => {
    const candidate_profile = new FormData()
    if (selectedFile) {
      candidate_profile.append('uploaded_resume', selectedFile)
    }
    candidate_profile.append('candidate_profile[interest]', interest)
    candidate_profile.append(
      'candidate_profile[seniority_level]',
      seniorityLevel,
    )
    candidate_profile.append('candidate_profile[job_title]', desiredJobTitle)
    candidate_profile.append('candidate_profile[industry]', desiredIndustry)
    candidate_profile.append(
      'candidate_profile[linked_in_profile_url]',
      linkedInProfile,
    )

    return apiPost('/candidate_profiles', candidate_profile, true).then(
      (res) => res.data,
    )
  }

  const mutation = useMutation({
    mutationFn,
    onSuccess: (res) => {
      console.log('Profile updated successfully', res)
      toggleShowUpdateProfileDialog()
      toast({ title: t('profile_updated') })
      queryClient.invalidateQueries(['candidateProfile'])
    },
    onError: (error) => {
      console.error('Error updating profile:', error)
      toast({
        title: t('error'),
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  return (
    <Modal
      title={
        <Stack direction="row" spacing={2}>
          <UserIcon className="h-6 w-6" />
          {t('title')}
        </Stack>
      }
      variant={'secondary'}
      size="md"
      open={showUpdateProfileDialog}
      fullHeight
      onClose={() => toggleShowUpdateProfileDialog()}
    >
      <div className="flex flex-col gap-y-4 px-4 pb-4">
        <p>{t('description')}</p>
        <div className="flex flex-row justify-between gap-x-4">
          <SelectField
            label={t('fields.interest')}
            className="w-full"
            value={interest}
            onValueChange={setInterest}
          >
            {interestKeys.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`interests.${option}`)}
              </SelectItem>
            ))}
          </SelectField>
          <SelectField
            label={t('fields.seniority_level')}
            className="w-full"
            value={seniorityLevel}
            onValueChange={setSeniorityLevel}
          >
            {seniorityKeys.map((option) => (
              <SelectItem key={option} value={t(`seniority_levels.${option}`)}>
                {t(`seniority_levels.${option}`)}
              </SelectItem>
            ))}
          </SelectField>
        </div>
        <TextField
          name="desired-job-title"
          label={t('fields.job_title')}
          value={desiredJobTitle}
          onChange={(e) => setDesiredJobTitle(e.target.value)}
        />
        <TextField
          name="desired-industry"
          label={t('fields.industry')}
          value={desiredIndustry}
          onChange={(e) => setDesiredIndustry(e.target.value)}
        />
        <div className="font-bold">{t('do_one_of_the_following')}</div>
        <TextField
          name="linkedin"
          label={t('fields.linked_in_profile_url')}
          value={linkedInProfile}
          onChange={(e) => setLinkedInProfile(e.target.value)}
        />
        <div className="">{t('linked_in_subtext')}</div>
        <div>
          <Dropzone
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          >
            {selectedFile && <div className="mb-4">{selectedFile.name}</div>}
          </Dropzone>
        </div>
        <TextArea
          name="career-history"
          label={t('fields.career_history')}
          value={careerHistory}
          onChange={(e) => setCareerHistory(e.target.value)}
        />
        <div className="flex flex-row justify-end gap-x-4">
          <Button
            variant="ghost"
            onClick={() => toggleShowUpdateProfileDialog()}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="coachcta"
            onClick={() => mutation.mutate()}
            disabled={mutation.isLoading}
          >
            {t('update_profile')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
const SelectField = ({ name, label, children, ...props }) => {
  return (
    <div className="flex flex-grow flex-col">
      <Label htmlFor={name}>{label}</Label>
      <Select id={name} className="mt-2" {...props}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="w-full">{children}</SelectContent>
      </Select>
    </div>
  )
}

const TextField = ({ name, label, value, onChange }) => {
  return (
    <div className="flex flex-col">
      <Label htmlFor={name}>{label}:</Label>
      <Input
        id={name}
        type="text"
        value={value}
        onChange={onChange}
        className="mt-2"
      />
    </div>
  )
}
const TextArea = ({ name, label, value, onChange }) => {
  return (
    <div className="flex flex-col">
      <Label className="leading-tight" htmlFor={name}>
        {label}
      </Label>
      <Textarea
        id={name}
        type="text"
        value={value}
        onChange={onChange}
        className="mt-2 min-h-16"
      />
    </div>
  )
}

export default UpdateCandidateProfileDialog
